// ============================================================
// Sample notifications for the showcase and for film mode.
//
// The bell reads GET /api/notifications/:investorId, which is blocked in a
// demo session — so it rendered empty, and "how does the app tell me about
// things" is exactly what anyone watching wants to see.
//
// VOICE: each one is written as work already in hand, not a task list. That is
// the point of managing a unit for someone — an owner abroad should learn that
// a deadline exists and that it is already handled in the same sentence. Keep
// new ones in that voice, and keep them true to what the PM service does.
//
// The moments that actually matter to a Dubai owner:
//
//   HANDOVER   an off-plan unit about a month from keys — snagging, final
//              payment, Ejari — the point where the app earns its place
//   RENEWAL    the 90-day notice window on a tenanted unit, which is a legal
//              deadline, not a nicety: miss it and the lease rolls on
//   PAYMENT    the next construction instalment on an off-plan unit
//   DEWA       the utility account that has to exist before handover day
//
// Every date is computed from today, so the set never ages into the past
// between one shoot and the next. They are read-only: marking one read
// updates local state and its POST is blocked, which is the correct
// behaviour for a session that must not write anything.
// ============================================================

import { HANDOVER_SOON } from './demoAssetDeskData';

const dayMs = 24 * 60 * 60 * 1000;
const daysAgo = (n: number): Date => new Date(Date.now() - n * dayMs);
const inDays = (n: number): Date => new Date(Date.now() + n * dayMs);

const dateLabel = (d: Date): string =>
  d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

/** Handover of the near-complete unit, off the same date the unit carries. */
const handoverDate = new Date(HANDOVER_SOON);
const handoverInDays = Math.max(1, Math.round((handoverDate.getTime() - Date.now()) / dayMs));

/**
 * The tenanted unit's lease ends 14 Jan 2027; Dubai practice is that either
 * side must give notice 90 days before. That window, not the end date, is the
 * deadline worth a notification.
 */
const leaseEnd = new Date('2027-01-14');
const noticeOpens = new Date(leaseEnd.getTime() - 90 * dayMs);
const noticeInDays = Math.round((noticeOpens.getTime() - Date.now()) / dayMs);

/** Next construction instalment on the off-plan unit. */
const instalmentDue = inDays(14);

export const DEMO_NOTIFICATIONS: any[] = [
  {
    id: 'demo-notif-handover',
    type: 'construction',
    title: `Handover in ${handoverInDays} days — Nautica Two · N2-4101`,
    message:
      `Construction is at 97% and the developer has confirmed handover for ${dateLabel(handoverDate)}. ` +
      'We have booked the snagging inspection and will attend on your behalf, send you the defect list with ' +
      'photos, and hold the developer to fixing it before you accept the keys. Nothing for you to travel for.',
    titleDe: `Übergabe in ${handoverInDays} Tagen — Nautica Two · N2-4101`,
    messageDe:
      `Der Bau steht bei 97%, und der Entwickler hat die Übergabe für den ${dateLabel(handoverDate)} bestätigt. ` +
      'Wir haben die Abnahme (Snagging) terminiert und nehmen sie für Sie wahr, senden Ihnen die Mängelliste mit ' +
      'Fotos und halten den Entwickler zur Behebung an, bevor Sie die Schlüssel annehmen. Sie müssen dafür nicht anreisen.',
    timestamp: daysAgo(1),
    read: false,
    unitId: 'demo-unit-nautica',
  },
  {
    id: 'demo-notif-payment',
    type: 'payment',
    title: 'Instalment due — Clearpoint Building 3 · 520',
    message:
      `AED 242,000 falls due to the developer on ${dateLabel(instalmentDue)} — the 40% construction milestone. ` +
      'We have checked it against your payment plan and the escrow account, and the transfer details are ready ' +
      'on the unit. We will chase the developer for the receipt and file it for you.',
    titleDe: 'Rate fällig — Clearpoint Building 3 · 520',
    messageDe:
      `AED 242.000 werden am ${dateLabel(instalmentDue)} an den Entwickler fällig — der Baufortschritt von 40%. ` +
      'Wir haben das mit Ihrem Zahlungsplan und dem Treuhandkonto abgeglichen, die Überweisungsdaten liegen bei der ' +
      'Einheit bereit. Die Quittung fordern wir für Sie an und legen sie ab.',
    timestamp: daysAgo(2),
    read: false,
    unitId: 'demo-unit-clearpoint',
  },
  {
    id: 'demo-notif-renewal',
    type: 'reminder',
    title:
      noticeInDays > 0
        ? `Renewal notice window opens in ${noticeInDays} days — Seagate Building 2 · 1204`
        : 'Renewal notice window is open — Seagate Building 2 · 1204',
    message:
      `The tenancy ends ${dateLabel(leaseEnd)} and notice must be served 90 days before — the window opens ` +
      `${dateLabel(noticeOpens)}. We have priced the renewal against the DLD rent index already. Approve the ` +
      'figure and we serve the notice, re-register Ejari and reschedule the cheques. You do not need to be here.',
    titleDe:
      noticeInDays > 0
        ? `Frist für die Verlängerung beginnt in ${noticeInDays} Tagen — Seagate Building 2 · 1204`
        : 'Die Frist für die Verlängerung läuft — Seagate Building 2 · 1204',
    messageDe:
      `Das Mietverhältnis endet am ${dateLabel(leaseEnd)}, und die Mitteilung muss 90 Tage vorher zugestellt werden — ` +
      `die Frist beginnt am ${dateLabel(noticeOpens)}. Wir haben die Verlängerung bereits anhand des DLD-Mietindex ` +
      'bepreist. Bestätigen Sie den Betrag, dann stellen wir zu, erneuern Ejari und terminieren die Schecks neu.',
    timestamp: daysAgo(4),
    read: false,
    unitId: 'demo-unit-seagate',
  },
  {
    // DEWA is the step that catches people out at handover: no account, no
    // power on the day you collect the keys, and the deposit differs for a
    // flat and a villa.
    id: 'demo-notif-dewa',
    type: 'document',
    title: 'DEWA activation — Nautica Two · N2-4101',
    message:
      'A unit cannot be handed over without a DEWA account, so we have started the activation with the premises ' +
      'number from your title deed — AED 2,000 deposit for an apartment plus the AED 130 connection fee, billed ' +
      'to your statement. Power and water will be live on handover day. Nothing for you to file.',
    titleDe: 'DEWA-Anmeldung — Nautica Two · N2-4101',
    messageDe:
      'Ohne DEWA-Konto kann keine Einheit übergeben werden — wir haben die Anmeldung mit der Premises-Nummer aus Ihrer ' +
      'Eigentumsurkunde bereits gestartet: AED 2.000 Kaution für eine Wohnung plus AED 130 Anschlussgebühr, über Ihre ' +
      'Abrechnung. Strom und Wasser laufen am Übergabetag. Sie müssen nichts einreichen.',
    timestamp: daysAgo(3),
    read: false,
    unitId: 'demo-unit-nautica',
  },
  {
    id: 'demo-notif-valuation',
    type: 'announcement',
    title: 'Your portfolio was revalued',
    message:
      'Seagate Building 2 is up 4.8% over six months on registered DLD transactions. Open Portfolio ▸ Valuation ' +
      'to see the comparable sales behind the number.',
    titleDe: 'Ihr Portfolio wurde neu bewertet',
    messageDe:
      'Seagate Building 2 liegt über sechs Monate 4,8% höher — auf Basis registrierter DLD-Transaktionen. Öffnen Sie ' +
      'Portfolio ▸ Bewertung, um die Vergleichsverkäufe hinter der Zahl zu sehen.',
    timestamp: daysAgo(6),
    read: true,
    unitId: 'demo-unit-seagate',
  },
];

// ============================================================
// Sample market notes for the dashboard's news strip.
//
// The live strip is served by GET /dubai-news, which a demo session cannot
// reach — so it fell back to items hardcoded in MainDashboard and dated
// Jan 2026, which on camera reads as an app nobody maintains.
//
// These are general market commentary in the company's own voice, dated off
// today, in both languages — a DACH viewer must not hit English mid-demo. Deliberately NOT attributed to a news outlet and carrying no link:
// inventing a headline and hanging a real publication's name on it is not a
// thing to ship, least of all into a film.
// ============================================================

const monthLabel = (d: Date): string => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

export const DEMO_NEWS: any[] = [
  {
    id: 'demo-news-1',
    title: 'Übergabesaison: was Sie prüfen, bevor Sie die Schlüssel annehmen',
    titleEn: 'Handover season: what to check before you accept the keys',
    summary: 'Abnahme, DEWA und Ejari in der richtigen Reihenfolge — die drei Wochen, die später Streit ersparen',
    summaryEn: 'Snagging, DEWA and Ejari in the right order — the three-week runway that saves a dispute later',
    date: monthLabel(new Date()),
    url: null,
  },
  {
    id: 'demo-news-2',
    title: 'Mieten beruhigen sich, während neues Angebot auf den Markt kommt',
    titleEn: 'Rents are settling as new supply lands',
    summary: 'Die Verlängerung richtet sich nach dem DLD-Index, nicht nach Angebotspreisen — was das bei der Verlängerung bedeutet',
    summaryEn: 'Renewal pricing now turns on the DLD index rather than the asking market — what that means at renewal',
    date: monthLabel(new Date()),
    url: null,
  },
  {
    id: 'demo-news-3',
    title: 'Waterfront-Lagen halten ihren Aufschlag',
    titleEn: 'Waterfront communities keep their premium',
    summary: 'Mina Rashid und Dubai Harbour liegen je sqft weiter über dem breiteren Wohnungsmarkt',
    summaryEn: 'Mina Rashid and Dubai Harbour still clear above the wider apartment market per sqft',
    date: monthLabel(new Date(Date.now() - 32 * 24 * 60 * 60 * 1000)),
    url: null,
  },
];

// ============================================================
// Dubai Sell inventory and upcoming launches for the dashboard.
//
// Both strips fetch from the backend (/api/sell-platform/listings and
// /upcoming-projects), which a demo session cannot reach — so the Dubai Sell
// strip hid itself entirely and "Upcoming Projects" rendered an empty heading.
// Two of the things the company actually sells, invisible in the demo.
//
// FICTIONAL units and launches in REAL communities, priced so the arithmetic
// on screen holds: marketValue > askingPrice, and discountPercent is
// (market − asking) / market, the same formula the backend applies. Photos
// reuse the project imagery the app already ships, so the cards are not empty
// grey boxes on camera.
// ============================================================

export const DEMO_PLATFORM_LISTINGS: any[] = [
  {
    id: 'demo-pl-1',
    title: 'Marina Gate — high floor, full marina view',
    titleDe: 'Marina Gate — hohe Etage, voller Marina-Blick',
    propertyType: 'Apartment',
    location: 'Dubai Marina',
    sizeSqft: 1180,
    bedrooms: 2,
    askingPrice: 2450000,
    marketValue: 2900000,
    isDistress: true,
    discountPercent: 16,
    discountBase: 'market',
    description: 'Owner relocating; sale to complete within eight weeks.',
    descriptionDe: 'Eigentümer zieht um; Abwicklung innerhalb von acht Wochen.',
    urgency: 'high',
    viewType: 'Full Marina View',
    unitPosition: 'corner',
    handoverDate: null,
    highlightTags: ['PRICE REDUCED', 'PRIME LOCATION', 'READY TO MOVE'],
    photoUrl: 'https://i.imgur.com/4lNc5Zb.jpeg',
  },
  {
    id: 'demo-pl-2',
    title: 'Sirdhana at Mina Rashid — waterfront, vacant on transfer',
    titleDe: 'Sirdhana in Mina Rashid — Wasserlage, bei Übergabe frei',
    propertyType: 'Apartment',
    location: 'Mina Rashid',
    sizeSqft: 1395,
    bedrooms: 2,
    askingPrice: 2980000,
    marketValue: 3420000,
    isDistress: true,
    discountPercent: 13,
    discountBase: 'market',
    description: 'Bank-instructed sale, vacant on transfer.',
    descriptionDe: 'Bankgeführter Verkauf, bei Übergabe bezugsfrei.',
    urgency: 'medium',
    viewType: 'Sea View',
    unitPosition: 'end',
    handoverDate: null,
    highlightTags: ['VACANT', 'PRIME LOCATION', 'EXCLUSIVE'],
    photoUrl: 'https://i.imgur.com/Wq09VFd.jpeg',
  },
  {
    id: 'demo-pl-3',
    title: 'Business Bay — studio, tenanted at 6.4%',
    titleDe: 'Business Bay — Studio, vermietet mit 6,4%',
    propertyType: 'Apartment',
    location: 'Business Bay',
    sizeSqft: 520,
    bedrooms: 1,
    askingPrice: 890000,
    marketValue: 1010000,
    isDistress: true,
    discountPercent: 12,
    discountBase: 'market',
    description: 'Tenanted until 2027, seller needs a quick exit.',
    descriptionDe: 'Vermietet bis 2027, Verkäufer benötigt einen schnellen Ausstieg.',
    urgency: 'medium',
    viewType: 'City View',
    unitPosition: null,
    handoverDate: null,
    highlightTags: ['HIGH ROI', 'NEW LISTING'],
    photoUrl: 'https://i.imgur.com/zhZ5t5s.jpeg',
  },
];

export const DEMO_UPCOMING_PROJECTS: any[] = [
  {
    id: 'demo-up-1',
    name: 'Seagate Building 5',
    developer: 'Emaar Properties',
    location: 'Mina Rashid, Dubai',
    images: ['https://i.imgur.com/Wq09VFd.jpeg'],
    launchDate: inDays(24).toISOString().slice(0, 10),
    status: 'active',
  },
  {
    id: 'demo-up-2',
    name: 'Nautica Three',
    developer: 'Select Group',
    location: 'Dubai Maritime City',
    images: ['https://i.imgur.com/4lNc5Zb.jpeg'],
    launchDate: inDays(46).toISOString().slice(0, 10),
    status: 'active',
  },
  {
    id: 'demo-up-3',
    name: 'Clearpoint Building 6',
    developer: 'Emaar Properties',
    location: 'Rashid Yachts & Marina, Dubai',
    images: ['https://i.imgur.com/HhXIcMP.jpeg'],
    launchDate: inDays(72).toISOString().slice(0, 10),
    status: 'active',
  },
];
